import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {PortalUsageApi} from '../../../api/portalUsageApi';
import {Spinner} from '../../Spinner';
import {Warning} from '../../icons';
import {PortalUsageBadge} from '../../portal_usage/PortalUsageBadge';
import {PortalUsageEmptyState} from '../../portal_usage/PortalUsageEmptyState';
import {PortalWeeklyChart} from '../../portal_usage/PortalWeeklyChart';
import {
    clientQueryCount,
    daysSince,
    relativeTimeLabel,
    resolveUsageState,
    stateHasActivity,
    USAGE_STATE,
    weeksStreak,
    weeksTotal,
} from '../../portal_usage/portalUsageState';
import {PortalAssistantTopics} from './PortalAssistantTopics';
import {PortalNavigationList} from './PortalNavigationList';
import {PortalQuestionsFeed} from './PortalQuestionsFeed';
import {PortalUsageActorSplit} from './PortalUsageActorSplit';
import {PortalUsageSummaryStrip} from './PortalUsageSummaryStrip';
import {PortalUsersUsage} from './PortalUsersUsage';

// Las dos ventanas del selector. 30 días contesta "¿lo está usando?"; 90 contesta "¿cuándo dejó de
// usarlo?", que es la pregunta del cliente dormido. El backend recorta cualquier valor a 90.
const WINDOWS = [30, 90];

/**
 * "Uso del portal y del asistente" — la sección que cierra el recorrido del tab Portal:
 * «a quién le di acceso» → **«qué hizo con él»**.
 *
 * Trae sus datos APARTE del resto del tab, con su propio spinner. No entra al `Promise.all` de
 * `ClientPortalTab` a propósito: aquel gatea el render entero de la pantalla con un spinner de
 * pantalla completa, y esta consulta es la más pesada de las cuatro (recorre el log de
 * interacciones). Meterla ahí haría que configurar el portal —que es lo que la agencia viene a
 * hacer— esperara a un cálculo analítico que ni siquiera pidió.
 */
export function PortalUsageSection({clientId, onInvite}) {
    const {t} = useTranslation();
    const [days, setDays] = useState(WINDOWS[0]);
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    // Si el PRIMER fetch nunca llegó a traer datos, la sección no existe para esta agencia.
    const [everLoaded, setEverLoaded] = useState(false);

    const fetchUsage = useCallback((windowDays) => {
        setLoading(true);
        setFailed(false);
        return PortalUsageApi.getClientUsage(clientId, {days: windowDays}).then((response) => {
            if (response && response.status === 'OK' && response.data) {
                setUsage(response.data);
                setEverLoaded(true);
            } else {
                // `APIClient` no distingue un 404 del gate de un 500 de verdad (el `error_code` del
                // cuerpo se pisa), así que la diferencia se hace por CUÁNDO falla, no por qué:
                //   - falla el primer fetch  → la sección no existe todavía para esta agencia
                //     (backend sin deployar, o plan sin portal) y se esconde en silencio. Ofrecer
                //     "Reintentar" ahí sería una caja de error permanente en la ficha de cada
                //     cliente, y no hay nada que reintentar.
                //   - falla después de haber cargado → algo pasó recién; ahí sí hay qué reintentar.
                // Nunca un cero: "no usa el portal" y "no pudimos preguntarlo" son afirmaciones
                // distintas y sólo una de las dos es sobre el cliente.
                setUsage(null);
                setFailed(true);
            }
            setLoading(false);
        });
    }, [clientId]);

    useEffect(() => {
        fetchUsage(days);
    }, [fetchUsage, days]);

    const header = (
        <div className="pu-box__head">
            <h3 className="pu-box__title">
                {t('portal_usage.section_title', 'Uso del portal y del asistente')}
            </h3>
            {usage ? <PortalUsageBadge state={resolveUsageState(usage)}/> : null}
            <div className="pu-box__actions">
                {/* La pregunta que sigue naturalmente a mirar un cliente es "¿y los demás?".
                    El link vive acá y no en el hero de la pantalla porque es donde aparece la
                    pregunta, y porque dos links idénticos en la misma pantalla no dan una segunda
                    opción. Sirve igual en el marco clásico y en el 2.1. */}
                <Link className="pu-rank__link" to="/agency/portal-usage">
                    {t('portal_usage.see_all_clients', 'Ver el uso en todos tus clientes')} &rarr;
                </Link>
                <div className="pu-window" role="group"
                     aria-label={t('portal_usage.window_group', 'Período')}>
                    {WINDOWS.map(option => (
                        <button
                            key={option}
                            type="button"
                            className={`pu-window__btn${option === days ? ' -on' : ''}`}
                            aria-pressed={option === days}
                            onClick={() => setDays(option)}
                        >
                            {t('portal_usage.window_days', 'Últimos {{days}} días', {days: option})}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );

    if (loading && !usage) {
        return (
            <div className="box pu-box">
                {header}
                <div className="box-loader"><Spinner color="positive"/></div>
            </div>
        );
    }

    if (failed || !usage) {
        if (!everLoaded) return null;
        return (
            <div className="box pu-box">
                {header}
                <PortalUsageEmptyState kind="ERROR" onRetry={() => fetchUsage(days)}/>
            </div>
        );
    }

    const state = resolveUsageState(usage);

    // Los tres estados sin actividad terminan acá: una sola caja con el vacío que corresponde. No
    // se muestran KPIs en cero ni un gráfico plano — con el cliente que nunca entró, ese vacío ES
    // la funcionalidad, y rodearlo de ceros lo convierte en una pantalla rota.
    if (!stateHasActivity(state)) {
        return (
            <div className="box pu-box">
                {header}
                <PortalUsageEmptyState
                    kind={state || 'ERROR'}
                    portalUsers={usage.portal_users}
                    onInvite={onInvite}
                    onRetry={() => fetchUsage(days)}
                />
            </div>
        );
    }

    const client = (usage.assistant && usage.assistant.client) || {};
    const dormantDays = state === USAGE_STATE.DORMANT ? daysSince(usage.last_activity_at) : null;

    return (
        <>
            <div className={`box pu-box${loading ? ' -reloading' : ''}`}>
                {header}

                {state === USAGE_STATE.DORMANT ? (
                    <div className="pu-notice -warn">
                        <Warning size={17}/>
                        <span>
                            <b>
                                {t('portal_usage.dormant_headline', 'Sin actividad desde hace {{n}} días.',
                                    {n: dormantDays})}
                            </b>{' '}
                            {t('portal_usage.dormant_detail', 'La última vez que usó el portal fue {{when}}.',
                                {when: relativeTimeLabel(usage.last_activity_at, t) || '—',
                                    interpolation: {escapeValue: false}})}
                        </span>
                    </div>
                ) : (
                    <p className="pu-box__sub">
                        {t('portal_usage.section_sub',
                            '{{queries}} consultas del cliente en los últimos {{days}} días, repartidas en {{activeDays}} días distintos.',
                            {
                                queries: clientQueryCount(usage),
                                days: usage.window_days,
                                activeDays: client.active_days || 0,
                            })}
                        {/* Sin «Su asistente hizo además N pasos»: «pasos» no se entendía (fede y
                            Leo, call del 07/10, card k14eI6RC). Sólo quedan adentro de cada consulta
                            desplegada, donde se ve qué hizo el asistente para responderla. */}
                    </p>
                )}

                <PortalUsageSummaryStrip usage={usage}/>
            </div>

            {/* La RESPUESTA antes que la evidencia: para qué lo usa, y recién después qué
                preguntó exactamente. */}
            <PortalAssistantTopics usage={usage}/>

            <div className="box pu-box">
                <div className="pu-box__head">
                    <h3 className="pu-box__title">
                        {t('portal_usage.chart_title', 'Consultas por semana')}
                    </h3>
                </div>
                <div className="pu-activity">
                    <div className="pu-activity__main">
                        <PortalWeeklyChart weeks={usage.weeks}/>
                    </div>
                    <div className="pu-activity__side">
                        <div className="pu-activity__side-title">
                            {t('portal_usage.pace_title', 'Cómo vuelve')}
                        </div>
                        <PaceRow n={client.active_days || 0}
                                 label={t('portal_usage.pace_active_days', 'días con al menos una consulta')}/>
                        <PaceRow n={weeksStreak(usage.weeks)}
                                 label={t('portal_usage.pace_streak', 'semanas seguidas con actividad')}/>
                        <PaceRow n={weeksTotal(usage.weeks)}
                                 label={t('portal_usage.pace_total', 'consultas en las últimas 12 semanas')}/>
                    </div>
                </div>
            </div>

            <div className="box pu-box">
                <div className="pu-box__head">
                    <h3 className="pu-box__title">{t('portal_usage.split_title', 'Quién consulta')}</h3>
                </div>
                <p className="pu-box__sub">
                    {t('portal_usage.split_sub',
                        'Sobre los datos de este cliente consultan las dos partes. Separarlo evita leer como '
                        + 'adopción lo que es trabajo interno del equipo.')}
                </p>
                <PortalUsageActorSplit assistant={usage.assistant}/>
            </div>

            {/* De «quién consulta» (la agencia o el cliente) a «quién del cliente»: Leo (Bakián)
                pidió ver el uso por persona, no sólo por cliente (card k14eI6RC). */}
            <PortalUsersUsage users={usage.portal_users} windowDays={usage.window_days}/>

            <PortalQuestionsFeed
                usage={usage}
                queryTextVisible={!!usage.query_text_visible}
            />

            <PortalNavigationList portalWeb={usage.portal_web}/>
        </>
    );
}

function PaceRow({n, label}) {
    return (
        <div className="pu-activity__row">
            <span className="pu-activity__n">{n}</span>
            <span className="pu-activity__l">{label}</span>
        </div>
    );
}

export default PortalUsageSection;
